import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';

/**
 * Reads a slice of an external store through `useSyncExternalStore`, so
 * every component sees the same store snapshot in a render (no tearing), and
 * re-renders only when the selected slice changes by `isEqual`. A selector
 * that builds a new object each call should pass an `isEqual` that compares
 * its fields; the previous value is then kept while they match.
 *
 * `subscribe` must be stable (module-level or memoized): a new one
 * resubscribes. `selector` and `isEqual` may change every render.
 */
export function useStoreSelector<TState, TSelected>(
    subscribe: (onStoreChange: () => void) => () => void,
    getState: () => TState,
    selector: (state: TState) => TSelected,
    isEqual: (left: TSelected, right: TSelected) => boolean = Object.is,
): TSelected {
    // The value last committed, so a fresh but equal selection can reuse it.
    const committedRef = useRef<{ value: TSelected } | null>(null);

    // Memoized per store snapshot: React may call getSnapshot repeatedly and
    // needs the same result while the store hasn't changed.
    const getSelection = useMemo(() => {
        let memo: { state: TState; value: TSelected } | null = null;
        return (): TSelected => {
            const state = getState();
            if (memo !== null && Object.is(memo.state, state)) {
                return memo.value;
            }
            const next = selector(state);
            const committed = committedRef.current;
            const value = committed !== null && isEqual(committed.value, next) ? committed.value : next;
            memo = { state, value };
            return value;
        };
    }, [getState, selector, isEqual]);

    const value = useSyncExternalStore(subscribe, getSelection);

    useEffect(() => {
        committedRef.current = { value };
    }, [value]);

    return value;
}

/**
 * Whether two objects hold the same values (by `Object.is`) under the same
 * own keys: the `isEqual` for a selector that builds a fresh object of
 * fields each call.
 */
export function isShallowEqual<T extends object>(left: T, right: T): boolean {
    if (Object.is(left, right)) {
        return true;
    }
    const leftKeys = Object.keys(left) as (keyof T)[];
    if (leftKeys.length !== Object.keys(right).length) {
        return false;
    }
    return leftKeys.every((key) => Object.hasOwn(right, key) && Object.is(left[key], right[key]));
}
